/* eslint-disable react/jsx-curly-newline */
import * as React from 'react';

import BicycleNumber from '@/components/elements/Icon/BicycleNumber';
import GradeInfo from '@/components/layouts/Race/Singlerace/RaceInfo/GradeInfo';
import RefundCard from '@/components/layouts/Race/Singlerace/RefundCard';
import {
  BetStatus,
  BetType,
  Odds,
  Race,
  RaceEntryStatus,
} from '@/lib/apollo/types/gen/graphql';
import {
  getBetTypeName,
  getVelodromeByCode,
  parseOddsInfo,
  setArrivalOrderFromRefund,
} from '@/lib/race';
import { RaceInfoType, RaceType } from '@/modelTypes';
import { raceSelector } from '@/selectors/race';

import ResultUI, { ResultTemplateProps } from '..';
import { ResultsNotExistText } from '../style';
import {
  ArraivalTableHeader,
  ButtonLink,
  CancelData,
  CancelText,
  CardHeader,
  DisabledLink,
  LinkWrapper,
  RacerInfo,
  RacerInfoWrapper,
  ResultCard,
  ResultCardWrapper,
  ResultNotExistText,
  Table,
  TableData,
  TableHeader,
  TableRow,
  TextLink,
  TextLinkArea,
  THead,
} from './style';

const SingleraceResultTemplate: React.FC<ResultTemplateProps> = ({
  raceProgram,
  racePrograms,
}) => {
  const getOdds = (odds?: Odds | null) =>
    odds ? parseOddsInfo(odds.oddsInfo) : null;

  const getEntryRacers = (race: Race, arrivalOrder: number) =>
    setArrivalOrderFromRefund(race.entryRacers, race.singleRaceRefunds).filter(
      er => er.arrivalOrder === arrivalOrder,
    );

  const basePath = (race: Race) =>
    `/${
      getVelodromeByCode(raceProgram.velodromeCode).romanName
    }/race/${raceProgram.heldDate.format('YYYY-MM-DD')}/${race.raceNum}`;

  const isCanceled = (race: Race) =>
    raceProgram.isCanceled || raceSelector(race).isCanceled;

  const isAllReturn = (race: Race) =>
    race.singleRaceRefunds.every(srr => srr.betStatus === BetStatus.AllReturn);

  return (
    <ResultUI
      raceProgram={raceProgram}
      racePrograms={racePrograms}
      raceType={RaceType.Singlerace}
    >
      {raceProgram.races.length ? (
        <ResultCardWrapper>
          {raceProgram.races.map(race => (
            <ResultCard key={race.id}>
              <CardHeader>
                <GradeInfo race={race} />
                {isCanceled(race) && <CancelText>中止</CancelText>}
              </CardHeader>
              {race.raceEntryStatus !== RaceEntryStatus.Unregistered ? (
                <>
                  <Table>
                    <THead>
                      <tr>
                        <ArraivalTableHeader>1着</ArraivalTableHeader>
                        <ArraivalTableHeader>2着</ArraivalTableHeader>
                        <ArraivalTableHeader>3着</ArraivalTableHeader>
                      </tr>
                    </THead>
                    <tbody>
                      <tr>
                        {[...Array(3)].map((_, i) => (
                          <td key={`${race.id}-${i}`}>
                            {isCanceled(race) ||
                            (isAllReturn(race) &&
                              getEntryRacers(race, i + 1).length === 0) ? (
                              <CancelData />
                            ) : (
                              <RacerInfoWrapper>
                                {getEntryRacers(race, i + 1).map((er, j) => (
                                  <RacerInfo key={`${er.id}-${j}`}>
                                    <BicycleNumber bicycleNum={er.bicycleNum}>
                                      {er.bicycleNum}
                                    </BicycleNumber>
                                    <p>{er.name}</p>
                                  </RacerInfo>
                                ))}
                              </RacerInfoWrapper>
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                  {isCanceled(race) ? (
                    <Table>
                      <tbody>
                        {Object.values(BetType).map((betType, i) => (
                          <TableRow key={`${betType}-${i}`}>
                            <TableHeader>{getBetTypeName(betType)}</TableHeader>
                            <TableData />
                          </TableRow>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <RefundCard
                      singleRaceRefunds={race.singleRaceRefunds}
                      odds={getOdds(race.odds)}
                      isCanceled={isCanceled(race)}
                    />
                  )}
                  <LinkWrapper>
                    <ButtonLink to={`${basePath(race)}`}>出走表</ButtonLink>
                    {isCanceled(race) ? (
                      <DisabledLink>オッズ</DisabledLink>
                    ) : (
                      <ButtonLink
                        to={`${basePath(race)}?infoType=${RaceInfoType.Odds}`}
                      >
                        オッズ
                      </ButtonLink>
                    )}
                  </LinkWrapper>
                  {!isCanceled(race) && (
                    <TextLinkArea>
                      <TextLink
                        to={`${basePath(race)}?infoType=${RaceInfoType.Result}`}
                      >
                        詳細を見る
                      </TextLink>
                    </TextLinkArea>
                  )}
                </>
              ) : (
                <ResultNotExistText>結果はまだありません</ResultNotExistText>
              )}
            </ResultCard>
          ))}
        </ResultCardWrapper>
      ) : (
        <ResultsNotExistText>レース結果はありません</ResultsNotExistText>
      )}
    </ResultUI>
  );
};

export default SingleraceResultTemplate;
